import type { MediaSelection, TimeRange } from '@mimir/common/util/timeRange';
import type { ArtifactReference, AudioTsSegmentRange, TsSegmentRange } from '@mimir/primitive/artifactReference';
import { isProxyOnlyArtifactReference } from '@mimir/primitive/artifactReference';
import type { DeliveryStrategy, DownloadBlocker, DownloadIdentity, DownloadPlan, DownloadProspect } from './downloadIdentity';
import type { ClientItem } from '@mimir/common/dto/searchResultHit';
import type { ItemArtefact } from '@mimir/common/dto/transferRequestDtos';
import { SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import { getItemSystemDefaultField } from '@mimir/common/mdf/MetaDataForm';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { MediaAvailability } from '@mimir/common/dto/mediaAvailability';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import { urlResolvableItemKeys } from '../storageManager/resolveTransferUrl';
import type { VirtualClipModel } from '@mimir/common/dto/virtualClipModel';
import { isInstanceOfVirtualClip } from '@mimir/common/dto/virtualClipUtil';
import { addFuzzy, divideFuzzy, fractionToFuzzy, integerToFuzzy, multiplyFuzzy, subtractFuzzy } from '@mimir/common/util/fuzzyMath';
import { getExtname } from '@mimir/common/platformAbstraction/pathComposition';
import { isInAdobeApp, type ApplicationContext } from '@mimir/common/context/applicationContext';
import type { BackendClient } from '@mimir/common/util/backendClient';
import { api_getItemFields } from '../util/api';
import { isCurrentlyLive } from '@mimir/common/item/media';

const downloadableItemKeys = <const>['id', 'media', 'aaf', 'itemType', 'virtualClipInfo', 'mediaDuration', 'originalFileName', 'indexUrl', 'isLive'];
export type DownloadableItem = Pick<ClientItem, typeof downloadableItemKeys[number] | typeof urlResolvableItemKeys[number]>;

/**
 * Context and input that affect which strategies we use for obtaining an
 * artifact.
 */
export interface DownloadRequestBase {
  isProxyWorkflowEnabled: boolean;
  isPremiereStandaloneMode: boolean;
}

export interface UnresolvedDownloadRequest extends DownloadRequestBase {
  item: DownloadableItem;
  requestedArtefact: ItemArtefact;

  /**
   * Approximate time range, relative to the virtual clip start.
   *
   * Note that unlike sourceMediaRange, this this timeRange needs to be
   * reinterpreted using the VirtualClipModel of an item, if any, before trying
   * to use the time range boundaries as media positions in the underlying
   * source media.
   */
  timeRange: TimeRange | null;

  /**
   * Potentially accurate time range, within the source media.
   */
  sourceMediaRange: MediaSelection;

  /**
   * For proxy only mode we should try to fetch as little as possible when
   * downloading the proxy even if this means that working in highres mode
   * in the future will not be possible for the clip.
   */
  proxyOnlyOptimizations: boolean;

  /**
   * If high-res sub clipping is enabled then any item with an associated
   * index file will imported as a sub section of the original media. This
   * means that the high res for these clips will be downloaded through the
   * S3 gateway using partial high res download (MXF subclipping etc.)
   *
   * Note that this will disable the direct use of high res is mounted folders
   */
  partialHighresDownload: boolean;

  /**
   * If set, then any TS segments being downloaded will be remuxed to mp4
   */
  remuxTsToMp4: boolean;

  wantGrowingArtifact: boolean;

  subclipExpansionRoom: number;
}

/**
 * Specialization for when the requester knows exactly what they want to
 * download.
 */
export interface IdentifiedDownloadRequest extends DownloadRequestBase {
  downloadIdentity: DownloadIdentity
}

export type DownloadRequest = IdentifiedDownloadRequest | UnresolvedDownloadRequest;

export function isIdentifiedDownloadRequest(req: DownloadRequest): req is IdentifiedDownloadRequest {
  const partial: Partial<IdentifiedDownloadRequest> = req;
  return partial.downloadIdentity != null;
}

export function isUnresolvedDownloadRequest(req: DownloadRequest): req is UnresolvedDownloadRequest {
  const partial: Partial<UnresolvedDownloadRequest> = req;
  return partial.item != null;
}

/**
 * Refreshes the item before planning download to make sure item states like the 'media' object
 * is up to date.
 */
export async function refreshItemAndPlanDownload(
  applicationContext: ApplicationContext,
  req: DownloadRequest,
  backendClient: BackendClient,
): Promise<DownloadProspect> {
  if (isIdentifiedDownloadRequest(req)) return planDownload(applicationContext, req);

  const freshItem: DownloadableItem = await api_getItemFields(
    backendClient,
    req.item.id,
    [...downloadableItemKeys, ...urlResolvableItemKeys]
  );
  return planDownload(applicationContext, { ...req, item: freshItem });
}

/**
 * Decide which strategies to use for obtaining an artifact.
 */
export function planDownload(applicationContext: ApplicationContext, req: DownloadRequest): DownloadProspect {
  let dlId: DownloadIdentity;
  let delivery: DeliveryStrategy;

  if (isIdentifiedDownloadRequest(req)) {
    dlId = req.downloadIdentity;
    delivery = getDeliveryStrategy(applicationContext, req);
  } else {
    const resolved = resolveDownloadPlan(applicationContext, req);
    if (!isValidPlan(resolved)) return resolved;
    dlId = resolved.dlId;
    delivery = resolved.deliver;
  }

  const validationResult = validateDownloadPlan(dlId, req);
  if (validationResult) return validationResult;

  return {
    blocker: null,
    delegate: appropriateDelegationStrategy(dlId, req),
    dlId,
    deliver: delivery
  };
}


function validateDownloadPlan(
  dlId: DownloadIdentity,
  req: DownloadRequest
): DownloadBlocker | null {
  if (dlId.artefact === 'highres' && isProxyOnlyArtifactReference(dlId.artifactReference)) {
    return { blocker: "Can't download highres for clip because it was imported with proxy-only optimizations enabled" };
  }

  return null;
}

function isValidPlan(prospect: DownloadProspect): prospect is DownloadPlan {
  return prospect.blocker === null;
}

function resolveDownloadPlan(applicationContext: ApplicationContext, req: UnresolvedDownloadRequest): DownloadProspect {
  const media = req.item.media;
  const artefact = req.requestedArtefact;
  const wantHighresOrEditProxy = artefact == 'highres' || artefact == 'proxy';

  if (wantHighresOrEditProxy && media != null) {
    const codecsByStreamId = media.codecsByStreamId;
    if (codecsByStreamId != null) {
      /*
        TODO: Introduce edit proxy stream role, and stop conflating it with web
        proxy stream role.

        NOTE: We use the TSSegmentRange artifact reference even if only the
        proxy is segmented TS. For instance for Kelda TS Live folder ingest
        we have a flat file highres, but the proxy is segmented TS. For cloud
        live both proxy and highres is segmented TS
      */
      const isAudioSegmentedTs = codecsByStreamId[StreamRoleId.LiveRecordAudioOnlyHighresTs] !== undefined;
      const isVideoSegmentedTs = codecsByStreamId[StreamRoleId.LiveRecordWebproxyTs] !== undefined;
      const hasHighResVideoTsSegments = codecsByStreamId[StreamRoleId.LiveRecordHighresTs] !== undefined;

      const flatHighres = isVideoSegmentedTs && !hasHighResVideoTsSegments;
      if (isVideoSegmentedTs || isAudioSegmentedTs) {
        return planTsSegmentDownload(req, media, artefact, flatHighres, isAudioSegmentedTs);
      }
    }
  }

  /*
    Virtual clip still requires the implicit delivery strategy, since it depends
    on code paths that dig out details such as title and such during the
    Premiere import step rather than planning everything ahead of time.

    TODO: Unify these code paths once we start storing accurate in and out
    points for virtual clips.
  */
  const vci = req.item.virtualClipInfo;
  const isVirtual = isInstanceOfVirtualClip(vci);
  if (isInAdobeApp(applicationContext) && !req.sourceMediaRange.whole && !isVirtual) {
    /*
      Plan downloading whole file as artifact, and import subclip into Premiere.

      This is used both for importing the full range of a virtual clip, as well
      as selected media ranges.
    */
    return {
      blocker: null,
      delegate: implicitDelegationStrategy(req.isPremiereStandaloneMode),
      dlId: {
        itemId: req.item.id,
        artefact,
        artifactReference: { type: 'ImplicitReference' },
      },
      deliver: {
        type: 'DeliverSubclip',
        whole: false,
        artifactRange: req.sourceMediaRange.bounds
      }
    };
  }

  const delivery = getDeliveryStrategy(applicationContext, req);
  return makeImplicitReferencePlan(req, artefact, delivery);
}

function planTsSegmentDownload(
  req: UnresolvedDownloadRequest,
  media: MediaAvailability,
  artefact: ItemArtefact,
  flatFileHighRes: boolean,
  audioTs: boolean,
): DownloadProspect {
  if (req.remuxTsToMp4 && req.wantGrowingArtifact) {
    return { blocker: 'Cannot download growing TS artifact when remux to MP4 is enabled' };
  }

  let first: number;
  let last: number;
  const sourceMediaRange = req.sourceMediaRange;
  const segmentDuration = fractionToFuzzy(media.segmentDuration);
  const bounds = sourceMediaRange.bounds;
  const whole = sourceMediaRange.whole;

  if (whole) {
    first = 0;
    last = media.segmentCount - 1;
  } else {
    const subclipExpansionRoom = { approximate: req.subclipExpansionRoom };
    const startForSubclip = subtractFuzzy(bounds.start, subclipExpansionRoom);
    const endForSubclip = addFuzzy(bounds.end, subclipExpansionRoom);
    const segStart = divideFuzzy(startForSubclip, segmentDuration);
    const segEnd = divideFuzzy(endForSubclip, segmentDuration);
    first = Math.max(Math.floor(segStart.approximate), 0);
    last = Math.min(Math.floor(segEnd.approximate), (media.segmentCount - 1));
  }

  let highResExt : string | undefined;

  // If we are importing the whole media there is no need for us to go through
  // the complexity of doing MXF subclipping so we just disable the index in
  // that case
  let useIndex : boolean = false;

  if (flatFileHighRes) {
    if (!req.item.originalFileName) {
      return { blocker: 'Expected item with flat file highres to have originalFileName' };
    }

    // We know that original filename will not contain any directories
    // so we can use any path separator here.
    const pathSep = '/';
    highResExt = getExtname(req.item.originalFileName, pathSep);

    // Enable the use of index file for partial highres download if the item has an
    // index file and the user has requested this kind of import.
    if (!whole && (req.item.indexUrl !== undefined) && req.partialHighresDownload) {
      useIndex = true;
    }

    // The user may also choose to make a proxy-only subclip when in proxy mode
    const proxyOnlyImport = (artefact === 'proxy') && req.proxyOnlyOptimizations;

    // If neither partial highres nor proxy only import is enabled then we must adjust the
    // first segment time to zero so that the full highres media can be linked with the
    // premiere subclip
    if (!useIndex && !proxyOnlyImport) {
      first = 0;
    }
  }

  const skipped = multiplyFuzzy(integerToFuzzy(first), segmentDuration);

  const artifactReference: TsSegmentRange | AudioTsSegmentRange = audioTs
    ? {
      type: 'AudioTsSegmentRange',
      itemId: req.item.id,
      segmentTimes: {
        first,
        last
      },
      remuxTo: req.remuxTsToMp4 ? 'fmp4' : undefined,
    }
    : {
      type: 'TsSegmentRange',
      itemId: req.item.id,
      segmentTimes: {
        first,
        last
      },
      highResExt,
      useIndex,
      remuxTo: (req.remuxTsToMp4 ? 'fmp4' : undefined),
      isGrowing:
        isCurrentlyLive(req.item) &&
        req.wantGrowingArtifact,
    };

  const delegate = implicitDelegationStrategy(req.isPremiereStandaloneMode);

  return {
    blocker: null,
    delegate,
    dlId: {
      itemId: req.item.id,
      artefact,
      artifactReference,
    },
    deliver: {
      type: 'DeliverSubclip',
      whole,
      artifactRange: {
        start: subtractFuzzy(bounds.start, skipped),
        end: subtractFuzzy(bounds.end, skipped)
      }

    }
  };
}

export function getDeliveryStrategy(applicationContext: ApplicationContext, req: DownloadRequest): DeliveryStrategy {
  return isInAdobeApp(applicationContext) ? { type: 'ImplicitImportPlan' } : { type: 'DeliverFile' };
}

function implicitDelegationStrategy(isPremiereStandaloneMode: boolean) {
  return isPremiereStandaloneMode ? 'Direct' : 'TransferRequest';
}

function appropriateDelegationStrategy(dlId: DownloadIdentity, req: DownloadRequest) {
  return implicitDelegationStrategy(req.isPremiereStandaloneMode);
}

function makeImplicitReferencePlan(req: UnresolvedDownloadRequest, artefact: ItemArtefact, delivery: DeliveryStrategy): DownloadPlan {
  return {
    blocker: null,
    delegate: implicitDelegationStrategy(req.isPremiereStandaloneMode),
    dlId: {
      itemId: req.item.id,
      artefact,
      artifactReference: { type: 'ImplicitReference' },
      firstSegmentIndex: req.timeRange?.start,
      lastSegmentIndex: req.timeRange?.end,
    },
    deliver: delivery
  };
}

export function makeTitle(plan: DownloadPlan, item: { metadata?: Partial<ItemMetaData> }, assignedTitle?: string) {
  // If a custom title is assigned to a file through import dialog, it will be used
  if (assignedTitle) return assignedTitle;

  const baseTitle = getItemSystemDefaultField(item, SystemDefaultField.Title)
    ?? item.metadata?.formData?.title // Special handling when 'fieldId' is used instead of 'id' in metadata (e.g. compact-view).
    ?? 'item';
  return baseTitle + getTitleSuffix(plan.dlId.artifactReference);
}

const subscripts: Record<string, string> = {
  '0': '₀',
  '1': '₁',
  '2': '₂',
  '3': '₃',
  '4': '₄',
  '5': '₅',
  '6': '₆',
  '7': '₇',
  '8': '₈',
  '9': '₉',
  '-': '₋',
};

export function getTitleSuffix(artifactReference: ArtifactReference) {
  switch (artifactReference.type) {
    case 'AudioTsSegmentRange': {
      const range = artifactReference.segmentTimes.first + '-' + artifactReference.segmentTimes.last;
      return ' ' + [...range].map(c => subscripts[c]).join('');
    }
    case 'TsSegmentRange': {
      const range = artifactReference.segmentTimes.first + '-' + (artifactReference.isGrowing ? '' : artifactReference.segmentTimes.last);
      return ' ' + [...range].map(c => subscripts[c]).join('');
    }
  }

  return '';
}

/**
 * Return the media source range of the whole virtual clip.
 */
export function getMediaSourceRangeOfWholeVirtualClip(virtualClipInfo: VirtualClipModel): MediaSelection {
  return {
    whole: false,
    bounds: {
      start: { approximate: virtualClipInfo.inPoint / 1000 },
      end: { approximate: virtualClipInfo.outPoint / 1000 }
    }
  };
}
